// CO - Oferta quantidade — comportamento do bloco conso-oferta-quantidade.liquid.
//
// Substitui o add-to-cart do widget `v-discount` do Zoorix. Duas diferenças que
// importam em relação ao que havia antes:
//
// 1. UMA chamada `/cart/add.js` com `items: [...]`, e não uma por unidade. O poll
//    `waitForCartSettled` em layout/theme.liquid (até 8s antes de abrir o drawer)
//    existe porque o carrinho passava por estados intermediários entre as adições
//    do Zoorix e o drawer pintava o desconto da 1ª unidade sozinha. Com uma
//    requisição só não há janela: a resposta já vem com o carrinho final.
// 2. As sections do carrinho voltam re-renderizadas no mesmo request
//    (`sections` + `sections_url`), então o drawer faz morph com o estado certo
//    sem round-trip extra.
//
// O `cart:update` é disparado SEM `window.__consoSilentCartUpdate`: isto é um
// add-to-cart de verdade, o drawer deve abrir (ver assets/conso-cart-drawer-guard.js).

if (!window.__consoOfertaQuantidadeInit) {
  window.__consoOfertaQuantidadeInit = true;

  const SOURCE_ID = 'conso-oferta-quantidade';
  const ERRO_GENERICO = 'Não foi possível adicionar ao carrinho. Tente novamente.';

  const cartAddUrl = () => window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const cartUrl = () => `${window.Theme?.routes?.cart_url || '/cart'}.js`;

  const getCartSectionIds = () => {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.getAttribute('data-section-id'))
      .filter(Boolean)
      .filter((sectionId, index, all) => all.indexOf(sectionId) === index);
  };

  // Espelha o `animateAddToCart` do product-form.js: o tema espera o
  // `animationend` e só então segura por 800ms. Aqui um teto fixo dá o mesmo
  // efeito sem depender do evento — se a animação não rodar (reduced motion,
  // por exemplo), o botão volta ao normal do mesmo jeito.
  const ADDED_RESET_MS = 1000;

  class ConsoOfertaQuantidade extends HTMLElement {
    #inFlight = false;
    #resetTimer = null;

    connectedCallback() {
      if (this.__consoBound) return;
      this.__consoBound = true;

      this.addEventListener('change', this.#handleChange);
      this.addEventListener('click', this.#handleClick);
    }

    get #selects() {
      return Array.from(this.querySelectorAll('[data-co-oq-select]'));
    }

    #handleChange = (event) => {
      const select = event.target.closest('[data-co-oq-select]');
      if (!select) return;

      const thumb = select.closest('.co-oq__slot')?.querySelector('[data-co-oq-thumb]');
      const src = select.selectedOptions[0]?.dataset.thumb;
      if (thumb && src) thumb.src = src;
    };

    #handleClick = (event) => {
      if (!event.target.closest('[data-co-oq-add]')) return;
      event.preventDefault();
      this.#add();
    };

    #setLoading(loading) {
      const button = this.querySelector('[data-co-oq-add]');
      if (!button) return;
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.classList.toggle('co-oq--loading', loading);
    }

    // As duas animações que o CTA do tema faz ao adicionar: o burst de check no
    // próprio botão e a miniatura voando até o ícone do carrinho. Ambas moram no
    // `add-to-cart-component`/`product-form.js`, que este bloco não usa (ele
    // espera um product form para submeter), então são reproduzidas aqui.
    #animate() {
      if (this.dataset.animarAdd === 'false') return;

      const button = this.querySelector('[data-co-oq-add]');
      if (!button) return;

      button.dataset.added = 'true';
      clearTimeout(this.#resetTimer);
      this.#resetTimer = setTimeout(() => button.removeAttribute('data-added'), ADDED_RESET_MS);

      const cartIcon = document.querySelector('.header-actions__cart-icon');
      const image = this.#selects[0]?.selectedOptions[0]?.dataset.thumb;
      if (!cartIcon || !image) return;

      const flyToCart = document.createElement('fly-to-cart');
      flyToCart.classList.add('fly-to-cart--main');
      flyToCart.style.setProperty('background-image', `url(${image})`);
      flyToCart.style.setProperty('--start-opacity', '0');
      flyToCart.source = button;
      flyToCart.destination = cartIcon;
      document.body.appendChild(flyToCart);
    }

    #setError(message) {
      const box = this.querySelector('[data-co-oq-error]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    }

    // Uma linha por variante distinta: escolher a mesma variante em dois selects
    // vira quantity 2, e não duas linhas iguais na sacola.
    #buildItems() {
      const items = [];

      for (const select of this.#selects) {
        const id = Number(select.value);
        if (!id) continue;

        const existing = items.find((item) => item.id === id);
        if (existing) existing.quantity += 1;
        else items.push({ id, quantity: 1 });
      }

      return items;
    }

    async #add() {
      if (this.#inFlight) return;

      const items = this.#buildItems();
      if (items.length === 0) return;

      this.#inFlight = true;
      this.#setLoading(true);
      this.#setError('');

      try {
        const body = { items };
        const sectionIds = getCartSectionIds();
        if (sectionIds.length > 0) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });

        const payload = await response.json();

        // `/cart/add.js` devolve 422 com `description` quando uma variante ficou
        // sem estoque entre o render da página e o clique — caso real num combo,
        // que leva minutos de escolha.
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || ERRO_GENERICO);
        }

        // Antes do GET /cart.js: a animação é feedback do clique e não deve
        // esperar um round-trip a mais para começar.
        this.#animate();

        const sections = payload.sections && typeof payload.sections === 'object' ? payload.sections : undefined;

        const cart = await fetch(cartUrl(), { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null);

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: SOURCE_ID,
              data: {
                itemCount: Number(cart?.item_count ?? 0),
                source: SOURCE_ID,
                sections,
              },
            },
          })
        );
      } catch (error) {
        this.#setError(error?.message || ERRO_GENERICO);
      } finally {
        this.#inFlight = false;
        this.#setLoading(false);
      }
    }
  }

  if (!customElements.get('conso-oferta-quantidade')) {
    customElements.define('conso-oferta-quantidade', ConsoOfertaQuantidade);
  }
}
